import React, { useCallback, useEffect, useState } from "react";
import { Pagination, Transfer, message, notification } from "antd";
import type { TransferItem } from "antd/es/transfer";
import { useParams } from "react-router-dom";
import {
  getBettingWebsiteByID,
  pullFromWebsiteBettingFeatures,
  pushIntoWebsiteBettingFeatures,
} from "../action";
import { getFeatures } from "../../Features/action";
import { IBettingWebsiteFeature } from "../../../types/BettingWebsite.Feature";
import { getCounter } from "../../../lib/functions";

const EditFeatures: React.FC<{ fetchData: () => void }> = ({ fetchData }) => {
  const { id: websiteId } = useParams();
  const [features, setFeatures] = useState<IBettingWebsiteFeature[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;
  const [searchQuery, setSearchQuery] = useState("");
  const [totalFeatures, setTotalFeatures] = useState(0); // if you can get total count

  const fetchCount = useCallback(async () => {
    try {
      const count = await getCounter("BettingWebsiteFeature");
      setTotalFeatures(count || 0);
    } catch (error) {
      message.error("Failed to fetch count");
    }
  }, []);

  // Fetch website selected features to set initial target keys
  const fetchWebsiteFeatures = async () => {
    if (!websiteId) return;
    setIsLoading(true);
    try {
      const website = await getBettingWebsiteByID(websiteId);
      const selectedIds =
        website?.Features?.map((f: IBettingWebsiteFeature) => String(f.id)) ||
        [];
      setTargetKeys(selectedIds);
    } catch (err: any) {
      api.error({
        message: "Failed to load website features",
        description: err?.message || "Unexpected error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch paginated & filtered feature list
  const fetchFeatures = async () => {
    setIsLoading(true);
    try {
      const res = await getFeatures(pageSize, currentPage - 1, searchQuery);
      setFeatures(res);
    } catch (err: any) {
      api.error({
        message: "Failed to fetch features",
        description: err.message || "Unexpected error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
  }, []);

  useEffect(() => {
    fetchWebsiteFeatures();
  }, [websiteId]);

  useEffect(() => {
    fetchFeatures();
  }, [currentPage, searchQuery]);

  // Handle transfer change (add/remove selected features)
  const handleChange = async (
    nextTargetKeys: (string | number)[],
    _direction: "left" | "right",
    _moveKeys: (string | number)[]
  ) => {
    const stringKeys = nextTargetKeys.map(String);
    const toPush = stringKeys.filter((id) => !targetKeys.includes(id));
    const toPull = targetKeys.filter((id) => !stringKeys.includes(id));

    setTargetKeys(stringKeys);
    setIsLoading(true);

    try {
      for (const idToPush of toPush) {
        await pushIntoWebsiteBettingFeatures(websiteId!, idToPush);
      }

      for (const idToPull of toPull) {
        await pullFromWebsiteBettingFeatures(websiteId!, idToPull);
      }

      api.success({ message: "Features updated successfully" });
      fetchData();
    } catch (err: any) {
      api.error({
        message: "Failed to update features",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input on left list
  const handleSearch = (direction: "left" | "right", value: string) => {
    if (direction === "left") {
      setSearchQuery(value);
      setCurrentPage(0); // reset page on new search
    }
  };

  // Prepare data for Transfer component
  const transferData: TransferItem[] = features.map((feat) => ({
    key: String(feat.id),
    title: feat.Feature?.En ?? "",
  }));

  // Disable "Next" if fewer than pageSize results were fetched (no more pages)
  const isNextDisabled = features.length < pageSize;

  return (
    <div className="space-y-5">
      {contextHolder}
      <Transfer
        dataSource={transferData}
        targetKeys={targetKeys}
        onChange={handleChange}
        onSearch={handleSearch}
        titles={["Available Features", "Selected Features"]}
        showSearch
        render={(item) => <span>{item.title}</span>}
        oneWay
        disabled={isLoading}
        style={{ width: "100%" }}
        listStyle={{
          width: "48%",
          height: 400,
        }}
      />{" "}
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={totalFeatures}
        onChange={(page) => setCurrentPage(page)}
        showSizeChanger={false}
        disabled={isLoading}
      />
    </div>
  );
};

export default EditFeatures;
